import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { adminService } from '@/services/admin.service';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Layout } from '@/components/Layout';
import { MaintenanceView } from '@/components/MaintenanceView';
import { isServerOrNetworkError } from '@/lib/utils';
import { Trash2, Loader2, AlertTriangle } from 'lucide-react';

export function TestingUsersPage() {
  const [confirmDelete, setConfirmDelete] = useState<{ id: string; email: string } | null>(null);
  const queryClient = useQueryClient();

  const { data: users = [], isLoading, error, refetch } = useQuery({
    queryKey: ['testingUsers'],
    queryFn: () => adminService.getTestingUsers(),
  });

  const deleteMutation = useMutation({
    mutationFn: (userId: string) => adminService.deleteTestingUser(userId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['testingUsers'] });
      setConfirmDelete(null);
      toast.success('User deleted permanently. Tenant, documents, and assets data have been removed.', {
        duration: 5000,
      });
      if (data?.driveFolderAttempted && data?.driveFolderDeleted === false) {
        toast.warning(
          'The "Our Digi Folio" folder in the user\'s Google Drive could not be removed (e.g. token expired or access revoked). They may still see it in their Drive.',
          { duration: 8000 },
        );
      }
    },
    onError: (error: { response?: { data?: { message?: string } }; message?: string }) => {
      const msg = error?.response?.data?.message || (error as Error)?.message || 'Failed to delete user.';
      toast.error(msg, { duration: 6000 });
    },
  });

  const handleDeleteClick = (id: string, email: string) => {
    setConfirmDelete({ id, email });
  };

  const handleConfirmDelete = () => {
    if (!confirmDelete) return;
    deleteMutation.mutate(String(confirmDelete.id));
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Testing – Users</h1>
          <p className="text-muted-foreground mt-1">
            Temporary section: list all users and permanently delete a user and all related data (tenant, documents, assets, Drive folder).
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Users</CardTitle>
            <CardDescription>All registered users. Delete removes the user and all their data.</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            )}
            {error && isServerOrNetworkError(error) && (
              <MaintenanceView
                onRetry={() => refetch()}
                retryLabel="Reload users"
              />
            )}
            {error && !isServerOrNetworkError(error) && (
              <div className="flex items-center gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-4 text-destructive">
                <AlertTriangle className="h-5 w-5 shrink-0" />
                <span>
                  {(error as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message ||
                    (error as Error)?.message ||
                    'Failed to load users'}
                </span>
              </div>
            )}
            {!isLoading && !error && users.length === 0 && (
              <p className="text-muted-foreground py-6 text-center">No users found.</p>
            )}
            {!isLoading && !error && users.length > 0 && (
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium">Email</th>
                      <th className="px-4 py-3 text-left font-medium">Name</th>
                      <th className="px-4 py-3 text-left font-medium">Created</th>
                      <th className="px-4 py-3 text-right font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={typeof u.id === 'string' ? u.id : u.email} className="border-t">
                        <td className="px-4 py-3">{u.email}</td>
                        <td className="px-4 py-3">{u.name ?? '—'}</td>
                        <td className="px-4 py-3">
                          {u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '—'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDeleteClick(String(u.id), u.email)}
                            disabled={deleteMutation.isPending}
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Delete
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Confirm delete modal */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="flex flex-row items-center gap-2 space-y-0">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              <CardTitle>Delete user permanently?</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                This will permanently delete <strong>{confirmDelete.email}</strong> and all related data: user record, tenant(s), documents, assets, and the &quot;Our Digi Folio&quot; folder in their Google Drive. This cannot be undone.
              </p>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setConfirmDelete(null)}
                  disabled={deleteMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleConfirmDelete}
                  disabled={deleteMutation.isPending}
                >
                  {deleteMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Deleting…
                    </>
                  ) : (
                    'Delete permanently'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </Layout>
  );
}
